import { useState, useMemo, useCallback, useEffect, useRef, memo } from 'react'
import type { ReactNode } from 'react'
import { Link, router } from '@inertiajs/react'
import { useReviewHeartbeat } from '@/hooks/useReviewHeartbeat'
import { useReviewShortcuts } from '@/hooks/useReviewShortcuts'
import ReviewLayout from '@/layouts/ReviewLayout'
import HoursDisplay from '@/components/admin/HoursDisplay'
import { WaitingLabel } from '@/components/admin/WaitingLabel'
import { ReviewStatusBadge } from '@/components/admin/ReviewStatusBadge'
import { ShortcutHelpDialog, type ShortcutEntry } from '@/components/admin/ShortcutHelpDialog'
import { Badge } from '@/components/admin/ui/badge'
import { Button } from '@/components/admin/ui/button'
import { Kbd } from '@/components/admin/ui/kbd'
import { Separator } from '@/components/admin/ui/separator'
import { Input } from '@/components/admin/ui/input'
import { Textarea } from '@/components/admin/ui/textarea'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/admin/ui/tooltip'
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/admin/ui/alert-dialog'
import {
  GitBranchIcon,
  CheckIcon,
  XCircleIcon,
  AlertTriangleIcon,
  MinusCircleIcon,
  ClockIcon,
  FlagIcon,
  MessageSquareTextIcon,
  LoaderIcon,
  GlobeIcon,
  ChevronDownIcon,
  LayoutGridIcon,
  FolderIcon,
  FolderOpenIcon,
  ClapperboardIcon,
} from 'lucide-react'
import ProjectNotesWindow from '@/components/admin/ProjectNotesWindow'
import RepoWorkspace from '@/components/admin/review/RepoWorkspace'
import WatchPanel from '@/components/admin/review/WatchPanel'
import { ReviewTabBar, ReviewTabPanel, ReviewTabsRoot, type ReviewTab } from '@/components/admin/review/ReviewTabs'
import RepoDiffCard from '@/components/admin/RepoDiffCard'
import SlackFeedbackComposer from '@/components/admin/SlackFeedbackComposer'
import { notify } from '@/lib/notifications'
import type {
  BuildReviewDetail,
  RequirementsCheckJournalEntry,
  PreflightCheck,
  RepoTreeData,
  RepoDiffData,
  RequirementsCheckProjectContext,
  ReviewerNote,
  SiblingReview,
  SiblingReviews,
  PreviousReview,
  MentionTarget,
  ReviewerSlackProps,
} from '@/types'

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

function isSafeUrl(url: string | null | undefined): boolean {
  if (!url) return false
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

function SiblingBadge({ label, review }: { label: string; review: SiblingReview }) {
  if (!review.status) return null
  const color =
    review.status === 'approved'
      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
      : review.status === 'returned' || review.status === 'rejected'
        ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400'
        : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
  const badge = (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${color}`}>
      {label}: {review.status}
      {review.reviewer && ` (${review.reviewer})`}
    </span>
  )
  return review.path ? (
    <Link href={review.path} className="hover:underline">
      {badge}
    </Link>
  ) : (
    badge
  )
}

// --- Collapsible Card ---

const JournalEntriesList = memo(function JournalEntriesList({ entries }: { entries: RequirementsCheckJournalEntry[] }) {
  return (
    <div className="divide-y divide-border overflow-y-auto max-h-96">
      {entries.map((entry) => (
        <div key={entry.id} className="p-3 space-y-2 min-w-0">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <img src={entry.author_avatar} alt="" className="size-4 rounded-full" />
            <span>{entry.author_display_name}</span>
            <span>|</span>
            <span>{entry.created_at}</span>
            <span className="flex items-center gap-1">
              <ClockIcon className="size-3" />
              {formatDuration(entry.total_duration)}
            </span>
            {!entry.in_ship && (
              <Badge
                variant="outline"
                className="text-[10px] bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800"
              >
                Not part of this ship
              </Badge>
            )}
          </div>

          {entry.recordings.length > 0 && (
            <div className="grid grid-cols-3 gap-1.5">
              {entry.recordings.map((rec) => (
                <div key={rec.id} className="text-xs rounded border border-border p-2 space-y-1">
                  <div className="flex items-center gap-1.5">
                    <Badge
                      className={`text-[10px] shrink-0 ${
                        rec.type === 'LookoutTimelapse'
                          ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800'
                          : rec.type === 'LapseTimelapse'
                            ? 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800'
                            : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800'
                      }`}
                      variant="outline"
                    >
                      {rec.type === 'LookoutTimelapse'
                        ? 'Lookout'
                        : rec.type === 'LapseTimelapse'
                          ? 'Lapse'
                          : 'YouTube'}
                    </Badge>
                    <span className="text-muted-foreground">{formatDuration(rec.duration)}</span>
                    {rec.removed_seconds > 0 && (
                      <span className="text-red-600 dark:text-red-400">
                        → {formatDuration(rec.duration - rec.removed_seconds)}
                      </span>
                    )}
                  </div>
                  {rec.description && <p className="text-muted-foreground leading-snug">{rec.description}</p>}
                </div>
              ))}
            </div>
          )}

          <div
            className="markdown-content max-w-none text-xs"
            style={{ zoom: 0.85 }}
            dangerouslySetInnerHTML={{ __html: entry.content_html }}
          />
          {entry.images.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {entry.images.map((img, j) => (
                <a key={j} href={img} target="_blank" rel="noopener noreferrer">
                  <img src={img} alt="" className="rounded border border-border object-cover w-full max-h-24" />
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
})

function CollapsibleCard({
  title,
  summary,
  defaultOpen = false,
  storageKey,
  borderClass,
  children,
  trailing,
}: {
  title: string
  summary?: React.ReactNode
  defaultOpen?: boolean
  storageKey?: string
  borderClass?: string
  children: React.ReactNode
  trailing?: React.ReactNode
}) {
  const [open, setOpen] = useState(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(`collapsible:${storageKey}`)
        if (saved !== null) return saved === '1'
      } catch {}
    }
    return defaultOpen
  })
  const toggle = () =>
    setOpen((v) => {
      const next = !v
      if (storageKey) {
        try {
          localStorage.setItem(`collapsible:${storageKey}`, next ? '1' : '0')
        } catch {}
      }
      return next
    })
  return (
    <div className={`rounded-md border overflow-hidden ${borderClass || 'border-border'}`}>
      <button
        onClick={toggle}
        className="w-full flex items-center gap-2 px-3 py-2 bg-muted/50 hover:bg-muted/80 transition-colors cursor-pointer text-left"
        data-card-key={storageKey}
      >
        <span className="text-sm font-semibold shrink-0">{title}</span>
        {summary && <span className="text-xs text-muted-foreground flex-1 min-w-0 truncate">{summary}</span>}
        {!summary && <span className="flex-1" />}
        {trailing}
        <ChevronDownIcon
          className={`size-3.5 shrink-0 text-muted-foreground transition-transform ${open ? '' : '-rotate-90'}`}
        />
      </button>
      {open && children}
    </div>
  )
}

// --- Preflight Results ---

function PreflightResults({ checks }: { checks: PreflightCheck[] }) {
  const [showPassed, setShowPassed] = useState(false)

  const failed = checks.filter((c) => c.status === 'failed')
  const warned = checks.filter((c) => c.status === 'warn')
  const skipped = checks.filter((c) => c.status === 'skipped')
  const passed = checks.filter((c) => c.status === 'passed')

  const issueCount = failed.length + warned.length + skipped.length
  const worstLevel = failed.length > 0 ? 'Fail' : warned.length > 0 ? 'Warn' : skipped.length > 0 ? 'Skip' : 'Pass'
  const levelColor =
    worstLevel === 'Fail'
      ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400'
      : worstLevel === 'Warn'
        ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400'
        : worstLevel === 'Skip'
          ? 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
          : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'

  const summaryNode = (
    <span className="flex items-center gap-1.5">
      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${levelColor}`}>{worstLevel}</span>
      <span>
        <span className="text-red-500 dark:text-red-400">{failed.length}</span>/
        <span className="text-amber-500 dark:text-amber-400">{warned.length}</span>/
        <span className="text-zinc-400 dark:text-zinc-500">{skipped.length}</span>/
        <span className="text-emerald-600 dark:text-emerald-400">{passed.length}</span>
      </span>
    </span>
  )

  return (
    <CollapsibleCard
      title="Preflight Checks"
      summary={summaryNode}
      defaultOpen={issueCount > 0}
      storageKey="build-preflight"
      borderClass={issueCount > 0 ? 'border-amber-300 dark:border-amber-800' : 'border-border'}
      trailing={<Kbd variant="muted">1</Kbd>}
    >
      <div className="p-3 space-y-2">
        {issueCount > 0 && (
          <div className="space-y-1">
            {failed.map((c) => (
              <div key={c.key} className="flex items-start gap-1.5 text-xs">
                <XCircleIcon className="size-3.5 shrink-0 text-red-500 dark:text-red-400 mt-0.5" />
                <span>
                  <strong className="text-foreground">{c.label}</strong>
                  {c.message && <span className="text-muted-foreground"> — {c.message}</span>}
                </span>
              </div>
            ))}
            {warned.map((c) => (
              <div key={c.key} className="flex items-start gap-1.5 text-xs">
                <AlertTriangleIcon className="size-3.5 shrink-0 text-amber-500 dark:text-amber-400 mt-0.5" />
                <span>
                  <strong className="text-foreground">{c.label}</strong>
                  {c.message && <span className="text-muted-foreground"> — {c.message}</span>}
                </span>
              </div>
            ))}
            {skipped.map((c) => (
              <div key={c.key} className="flex items-start gap-1.5 text-xs">
                <MinusCircleIcon className="size-3.5 shrink-0 text-zinc-400 dark:text-zinc-500 mt-0.5" />
                <span>
                  <strong className="text-muted-foreground">{c.label}</strong>
                  {c.message && <span className="text-muted-foreground"> — {c.message}</span>}
                </span>
              </div>
            ))}
          </div>
        )}

        {passed.length > 0 && (
          <div>
            <button
              onClick={() => setShowPassed((v) => !v)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              {showPassed ? 'Hide' : 'Show'} passed ({passed.length})
            </button>
            {showPassed && (
              <div className="mt-1 space-y-0.5">
                {passed.map((c) => (
                  <div key={c.key} className="flex items-start gap-1.5 text-xs">
                    <CheckIcon className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400 mt-0.5" />
                    <span>
                      <strong className="text-muted-foreground">{c.label}</strong>
                      {c.message && <span className="text-muted-foreground"> — {c.message}</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </CollapsibleCard>
  )
}

type ReviewView = 'overview' | 'repo' | 'watch'

const REVIEW_TABS: ReviewTab[] = [
  { id: 'overview', title: 'Overview', icon: LayoutGridIcon, tint: 'green' },
  { id: 'repo', title: 'Repo', icon: FolderIcon, activeIcon: FolderOpenIcon, tint: 'violet' },
  { id: 'watch', title: 'Watch', icon: ClapperboardIcon, tint: 'blue' },
]

// --- Top Bar ---

function TopBar({
  project,
  notesCount,
  projectFlagged,
  flagging,
  endSessionHref,
  showFlag = true,
  center,
  onSkip,
  onToggleNotes,
  onFlag,
}: {
  project: RequirementsCheckProjectContext
  notesCount: number
  projectFlagged: boolean
  flagging: boolean
  endSessionHref: string
  showFlag?: boolean
  center?: ReactNode
  onSkip: () => void
  onToggleNotes: () => void
  onFlag: (reason: string) => void
}) {
  const [flagReason, setFlagReason] = useState('')

  return (
    <TooltipProvider delayDuration={150}>
      <div className="relative z-50 bg-muted/40 border-b border-border px-4 py-3 flex flex-wrap items-center gap-2 shrink-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="default" asChild>
              <Link href={endSessionHref}>
                End Session
                <Kbd variant="muted" className="ml-1">
                  E
                </Kbd>
              </Link>
            </Button>
          </TooltipTrigger>
          <TooltipContent>End session</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="default" onClick={onSkip}>
              Skip
              <Kbd variant="muted" className="ml-1">
                S
              </Kbd>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Skip to next</TooltipContent>
        </Tooltip>

        <Separator orientation="vertical" className="h-6 hidden sm:block" />

        <a
          href={`/admin/projects/${project.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold truncate hover:underline"
        >
          {project.name}
        </a>
        <span className="text-sm text-muted-foreground hidden sm:inline">
          by{' '}
          <a
            href={`/admin/users/${project.user_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline text-foreground"
          >
            {project.user_display_name}
          </a>
          {project.collaborators.length > 0 && (
            <>
              {project.collaborators.map((c, i) => (
                <span key={c.id}>
                  {i === 0 ? ' with ' : ', '}
                  <a
                    href={`/admin/users/${c.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline text-foreground"
                  >
                    {c.display_name}
                  </a>
                </span>
              ))}
            </>
          )}
        </span>

        {/* Centered on the bar itself (not between the clusters), mirroring the yahri header grid */}
        {center && <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">{center}</div>}

        <div className="ml-auto flex items-center flex-wrap gap-2 shrink-0">
          {isSafeUrl(project.repo_link) && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="default" asChild>
                    <a href={project.repo_link!} target="_blank" rel="noopener noreferrer">
                      <GitBranchIcon data-icon="inline-start" />
                      Repo
                      <Kbd variant="muted" className="ml-1">
                        G
                      </Kbd>
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open repo on GitHub</TooltipContent>
              </Tooltip>
            </>
          )}
          {isSafeUrl(project.demo_link) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="default" asChild>
                  <a href={project.demo_link!} target="_blank" rel="noopener noreferrer">
                    <GlobeIcon data-icon="inline-start" />
                    Demo
                    <Kbd variant="muted" className="ml-1">
                      D
                    </Kbd>
                  </a>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Open demo</TooltipContent>
            </Tooltip>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="default" onClick={onToggleNotes}>
                <MessageSquareTextIcon data-icon="inline-start" />
                Notes{notesCount > 0 && ` (${notesCount})`}
                <Kbd variant="muted" className="ml-1">
                  N
                </Kbd>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Toggle notes</TooltipContent>
          </Tooltip>

          {showFlag &&
            (projectFlagged ? (
              <Badge variant="destructive">Flagged</Badge>
            ) : (
              <AlertDialog>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="icon" aria-label="Flag project">
                        <FlagIcon />
                      </Button>
                    </AlertDialogTrigger>
                  </TooltipTrigger>
                  <TooltipContent>Flag project</TooltipContent>
                </Tooltip>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Flag Project for Fraud</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will remove the project from all review queues. The user will not be notified — the project
                      will still appear as pending to them.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <Textarea
                    placeholder="Reason for flagging..."
                    value={flagReason}
                    onChange={(e) => setFlagReason(e.target.value)}
                    className="min-h-20"
                  />
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      variant="destructive"
                      disabled={!flagReason.trim() || flagging}
                      onClick={() => onFlag(flagReason.trim())}
                    >
                      Flag Project
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ))}
        </div>
      </div>
    </TooltipProvider>
  )
}

// --- Main Page ---

interface PageProps {
  review: BuildReviewDetail
  project: RequirementsCheckProjectContext
  new_entries: RequirementsCheckJournalEntry[]
  previous_entries: RequirementsCheckJournalEntry[]
  sibling_statuses: SiblingReviews
  previous_reviews: PreviousReview[]
  repo_tree?: RepoTreeData | null
  repo_diff?: RepoDiffData | null
  reviewer_notes?: ReviewerNote[]
  reviewer_notes_path: string
  project_flagged: boolean
  reviewer_slack?: ReviewerSlackProps
  pending_conversion_koi: number
  can: { update: boolean; swap_type: boolean }
  skip: string | null
  heartbeat_path: string
  swap_type_path: string | null
  next_path: string
  index_path: string
  backfill?: boolean
}

export default function BuildReviewsShow({
  review,
  project,
  new_entries,
  previous_entries,
  sibling_statuses,
  previous_reviews,
  repo_tree,
  repo_diff,
  reviewer_notes,
  reviewer_notes_path,
  project_flagged,
  reviewer_slack,
  pending_conversion_koi,
  can,
  skip,
  heartbeat_path,
  swap_type_path,
  next_path,
  index_path,
  backfill = false,
}: PageProps) {
  const isTerminal = review.status !== 'pending'
  // Backfill edits an approved (terminal) review but still holds a live claim, so heartbeat regardless.
  useReviewHeartbeat(heartbeat_path, backfill || !isTerminal)

  const [feedback, setFeedback] = useState(review.feedback || '')
  const [feedbackMentions, setFeedbackMentions] = useState<MentionTarget[]>([])
  const [postToSlack, setPostToSlack] = useState(true)
  const [internalReason, setInternalReason] = useState(review.internal_reason || '')
  const [hoursAdjInput, setHoursAdjInput] = useState(
    review.hours_adjustment != null ? String(review.hours_adjustment / 3600) : '',
  )
  const [goldAdjInput, setGoldAdjInput] = useState(review.gold_adjustment != null ? String(review.gold_adjustment) : '')
  const [demoLinkInput, setDemoLinkInput] = useState(project.demo_link || project.demo_video_link || '')
  const [submitting, setSubmitting] = useState(false)
  const [notesOpen, setNotesOpen] = useState(false)
  const [flagging, setFlagging] = useState(false)
  const [isFlagged, setIsFlagged] = useState(project_flagged)
  const [notes, setNotes] = useState<ReviewerNote[]>(reviewer_notes ?? [])
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [view, setView] = useState<ReviewView>('overview')
  const [designDialogOpen, setDesignDialogOpen] = useState(false)
  const feedbackRef = useRef<HTMLTextAreaElement>(null)
  const internalReasonRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (reviewer_notes) setNotes(reviewer_notes)
  }, [reviewer_notes])

  const userFacingHours = project.approved_public_hours ?? project.logged_hours
  const hoursAdj = hoursAdjInput !== '' ? parseFloat(hoursAdjInput) || 0 : 0
  const internalHours = userFacingHours + hoursAdj
  const baseGold = Math.floor(7 * userFacingHours) // Gold based on user-facing hours (TA-approved)
  const goldAdj = goldAdjInput !== '' ? parseInt(goldAdjInput, 10) || 0 : 0
  const finalGold = baseGold + goldAdj

  const preflight = useMemo(() => {
    const results = review.preflight_results || []
    return results
  }, [review.preflight_results])

  const allEntries = useMemo(() => [...new_entries, ...previous_entries], [new_entries, previous_entries])

  const handleSkip = useCallback(() => {
    const skipIds = skip ? skip.split(',') : []
    skipIds.push(String(review.id))
    router.visit(`${next_path}?skip=${skipIds.join(',')}`)
  }, [skip, review.id, next_path])

  const handleFlag = useCallback(
    async (reason: string) => {
      setFlagging(true)
      try {
        const res = await fetch('/admin/project_flags', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-Token': csrfToken(),
          },
          body: JSON.stringify({
            project_flag: {
              project_id: project.id,
              ship_id: review.ship_id,
              review_stage: 'build_review',
              reason,
            },
          }),
        })
        if (res.ok) {
          setIsFlagged(true)
          const skipIds = skip ? skip.split(',') : []
          skipIds.push(String(review.id))
          router.visit(`${next_path}?skip=${skipIds.join(',')}`)
        }
      } finally {
        setFlagging(false)
      }
    },
    [project.id, review.ship_id, review.id, skip, next_path],
  )

  const handleSubmit = useCallback(
    (status: 'approved' | 'returned') => {
      setSubmitting(true)
      const url = skip
        ? `/admin/reviews/build_reviews/${review.id}?skip=${skip}`
        : `/admin/reviews/build_reviews/${review.id}`
      const hoursAdjSeconds = hoursAdjInput !== '' ? Math.round((parseFloat(hoursAdjInput) || 0) * 3600) : null
      const goldAdjValue = goldAdjInput !== '' ? parseInt(goldAdjInput, 10) || 0 : null
      router.patch(
        url,
        {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          build_review: {
            status,
            feedback: feedback.trim() || null,
            internal_reason: internalReason.trim() || null,
            hours_adjustment: hoursAdjSeconds,
            gold_adjustment: goldAdjValue,
          } as any,
          demo_link: demoLinkInput.trim(),
          post_to_slack: postToSlack,
          feedback_mention_ids: feedbackMentions.map((m) => m.id),
        },
        {
          onSuccess: () => {
            setFeedback('')
            setFeedbackMentions([])
            setInternalReason('')
          },
          onError: (errs) => {
            const entries = Object.entries(errs as Record<string, string | string[]>)
            if (entries.length === 0) {
              notify('alert', 'Could not submit review. Please try again.')
              return
            }
            const message = entries
              .map(([field, val]) => {
                const msg = Array.isArray(val) ? val.join(', ') : val
                const label = field.replace(/_/g, ' ')
                return `${label}: ${msg}`
              })
              .join('; ')
            notify('alert', `Could not submit review — ${message}`)
          },
          onFinish: () => setSubmitting(false),
        },
      )
    },
    [
      review.id,
      feedback,
      feedbackMentions,
      postToSlack,
      internalReason,
      hoursAdjInput,
      goldAdjInput,
      demoLinkInput,
      skip,
    ],
  )

  // Backfill: save only the internal fields (internal reason + hours) plus the editable
  // demo link. Feedback, status, and gold are frozen. Advances to the next-oldest review.
  const handleBackfillSave = useCallback(() => {
    if (submitting || !internalReason.trim()) return
    setSubmitting(true)
    const url = skip ? `${index_path}/${review.id}?skip=${skip}` : `${index_path}/${review.id}`
    const hoursAdjSeconds = hoursAdjInput !== '' ? Math.round((parseFloat(hoursAdjInput) || 0) * 3600) : null
    router.patch(
      url,
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        build_review: {
          internal_reason: internalReason.trim() || null,
          hours_adjustment: hoursAdjSeconds,
        } as any,
        demo_link: demoLinkInput.trim(),
      },
      {
        onError: (errs) => {
          const message = Object.entries(errs as Record<string, string | string[]>)
            .map(([field, val]) => `${field.replace(/_/g, ' ')}: ${Array.isArray(val) ? val.join(', ') : val}`)
            .join('; ')
          notify('alert', `Could not save backfill${message ? ` — ${message}` : ''}`)
        },
        onFinish: () => setSubmitting(false),
      },
    )
  }, [submitting, internalReason, hoursAdjInput, demoLinkInput, skip, index_path, review.id])

  // Open a URL in a new tab — shared between toolbar buttons and shortcut handlers.
  const openExternal = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [])

  // Cmd/Ctrl+Enter inside a textarea: save the backfill, or choose between Approve and
  // Return — Approve when an internal reason is present, otherwise treat a non-empty
  // feedback draft as a Return.
  const handleModifierEnter = useCallback(() => {
    if (backfill) {
      handleBackfillSave()
      return
    }
    if (isTerminal || submitting) return
    if (internalReason.trim()) {
      handleSubmit('approved')
    } else if (feedback.trim()) {
      handleSubmit('returned')
    }
  }, [backfill, handleBackfillSave, isTerminal, submitting, feedback, internalReason, handleSubmit])

  const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform)
  const modKey = isMac ? '⌘' : 'Ctrl'

  const shortcutEntries: ShortcutEntry[] = useMemo(
    () => [
      { combo: [modKey, 'P'], key: 'cmd+p', description: 'Approve' },
      { combo: [modKey, 'E'], key: 'cmd+e', description: 'Return (focus feedback if empty)' },
      { key: 'S', description: 'Skip to next review' },
      { key: 'E', description: 'End session' },
      { key: 'N', description: 'Toggle reviewer notes' },
      { key: 'U', description: 'Open user in new tab' },
      { key: 'G', description: 'Open repo on GitHub' },
      { key: 'H', description: 'Open repo in HURT' },
      { key: 'D', description: 'Open demo link' },
      { key: 'C', description: 'Copy repo URL' },
      { combo: [modKey, 'D'], key: 'cmd+d', description: 'Move to Design Review' },
      { combo: [modKey, 'J'], key: 'cmd+j', description: 'Focus Internal Reason' },
      { combo: [modKey, 'F'], key: 'cmd+f', description: 'Focus Feedback' },
      { combo: [modKey, '↵'], key: 'cmd+enter', description: 'Submit current draft' },
      { key: '?', description: 'Show this cheatsheet' },
      { key: '1', description: 'Toggle Preflight Checks' },
      { key: '2', description: 'Toggle Previous Reviews' },
      { key: 'O', description: 'Overview view' },
      { key: '3', description: 'Repo view' },
      { key: 'W', description: 'Watch view' },
      { key: '4', description: 'Toggle Journal' },
      { key: '5', description: 'Toggle Changes Since Last Review' },
    ],
    [modKey],
  )

  // Card toggles live in the Overview view — switch there first so the toggle is visible.
  const toggleOverviewCard = useCallback((key: string) => {
    setView('overview')
    ;(document.querySelector(`[data-card-key="${key}"]`) as HTMLElement | null)?.click()
  }, [])

  useReviewShortcuts({
    p: {
      handler: () => {
        if (backfill || isTerminal || submitting) return
        handleSubmit('approved')
      },
      requireModifier: true,
    },
    e: {
      handler: (ev) => {
        if (ev.metaKey || ev.ctrlKey) {
          // ⌘E — Return
          if (backfill || isTerminal || submitting) return
          if (!feedback.trim()) {
            feedbackRef.current?.focus()
          } else {
            handleSubmit('returned')
          }
        } else {
          // E — End session
          router.visit(index_path)
        }
      },
      acceptsModifier: true,
    },
    s: { handler: () => !isTerminal && !submitting && handleSkip() },
    n: { handler: () => setNotesOpen((v) => !v) },
    u: { handler: () => openExternal(`/admin/users/${project.user_id}`) },
    g: {
      handler: () => {
        if (isSafeUrl(project.repo_link)) openExternal(project.repo_link!)
      },
    },
    h: {
      handler: () => {
        if (isSafeUrl(project.repo_link)) {
          openExternal(`https://hurt-xi.vercel.app/?repo=${encodeURIComponent(project.repo_link!)}`)
        }
      },
    },
    d: {
      handler: (ev) => {
        if (ev.metaKey || ev.ctrlKey) {
          // ⌘D — Move to Design Review
          if (can.swap_type && !isTerminal && !submitting) setDesignDialogOpen(true)
        } else if (isSafeUrl(project.demo_link)) {
          openExternal(project.demo_link!)
        }
      },
      acceptsModifier: true,
    },
    c: {
      handler: () => {
        if (project.repo_link) navigator.clipboard.writeText(project.repo_link)
      },
    },
    '?': { handler: () => setShortcutsOpen((v) => !v) },
    enter: { handler: handleModifierEnter, allowInTyping: true, requireModifier: true },
    j: {
      handler: () => {
        internalReasonRef.current?.focus()
        internalReasonRef.current?.select()
      },
      requireModifier: true,
    },
    f: {
      handler: () => {
        feedbackRef.current?.focus()
        feedbackRef.current?.select()
      },
      requireModifier: true,
    },
    o: { handler: () => setView('overview') },
    '1': { handler: () => toggleOverviewCard('build-preflight') },
    '2': { handler: () => toggleOverviewCard('build-previous-reviews') },
    '3': { handler: () => setView('repo') },
    w: { handler: () => setView('watch') },
    '4': { handler: () => toggleOverviewCard('build-journal') },
    '5': { handler: () => toggleOverviewCard('build-repo-diff') },
  })

  return (
    <>
      <ReviewTabsRoot value={view} onValueChange={(v) => setView(v as ReviewView)} asChild>
        <div className="h-screen flex flex-col overflow-hidden border-t-3 border-orange-500">
          <TopBar
            center={<ReviewTabBar tabs={REVIEW_TABS} value={view} onValueChange={(v) => setView(v as ReviewView)} />}
            project={project}
            notesCount={notes.length}
            projectFlagged={isFlagged}
            flagging={flagging}
            endSessionHref={index_path}
            showFlag={!backfill}
            onSkip={handleSkip}
            onToggleNotes={() => setNotesOpen((v) => !v)}
            onFlag={handleFlag}
          />

          {notesOpen && reviewer_notes && (
            <ProjectNotesWindow
              notes={notes}
              setNotes={setNotes}
              notesPath={reviewer_notes_path}
              shipId={review.ship_id}
              reviewStage="build_review"
              onClose={() => setNotesOpen(false)}
            />
          )}

          <div className="flex-1 min-h-0 flex">
            <ReviewTabPanel value="overview">
              {/* Left: project info + preflight + journal */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Project overview */}
                <div className="rounded-md border border-border overflow-hidden">
                  <div className="p-3 space-y-1">
                    <h1 className="text-base font-semibold leading-snug">
                      <a
                        href={`/admin/projects/${project.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        {project.name}
                      </a>
                    </h1>
                    {project.description && (
                      <p className="text-sm text-muted-foreground leading-relaxed">{project.description}</p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                      <a
                        href={`/admin/users/${project.user_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-foreground hover:underline"
                      >
                        <img src={project.user_avatar} alt="" className="size-4 rounded-full" />
                        <span>{project.user_display_name}</span>
                      </a>
                      {project.collaborators.map((c) => (
                        <a
                          key={c.id}
                          href={`/admin/users/${c.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-foreground hover:underline"
                        >
                          <img src={c.avatar} alt="" className="size-4 rounded-full" />
                          <span>{c.display_name}</span>
                        </a>
                      ))}
                      <span>|</span>
                      <span>{project.created_at}</span>
                      {project.tags.length > 0 && (
                        <>
                          <span>|</span>
                          <span className="text-foreground">{project.tags.join(', ')}</span>
                        </>
                      )}
                      <span>|</span>
                      <WaitingLabel waitingSince={project.waiting_since} cycleStartedAt={project.cycle_started_at} />
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 divide-x divide-border border-t border-border">
                    <div className="px-3 py-2">
                      <p className="text-xs text-muted-foreground mb-0.5">Type</p>
                      <p className="text-sm font-medium capitalize">{project.ship_type}</p>
                    </div>
                    <div className="px-3 py-2">
                      <p className="text-xs text-muted-foreground mb-0.5">Hours Approved</p>
                      <p className="text-sm">
                        <HoursDisplay
                          publicHours={project.approved_public_hours}
                          internalHours={project.approved_internal_hours}
                          loggedHours={project.ship_logged_hours}
                        />
                      </p>
                    </div>
                    <div className="px-3 py-2">
                      <p className="text-xs text-muted-foreground mb-0.5">Entries</p>
                      <p className="text-sm font-mono">{project.entry_count}</p>
                    </div>
                  </div>

                  {/* Links row */}
                  {(isSafeUrl(project.frozen_repo_link) || isSafeUrl(project.frozen_demo_link)) && (
                    <div
                      className={`grid divide-x divide-border border-t border-border ${
                        isSafeUrl(project.frozen_repo_link) && isSafeUrl(project.frozen_demo_link)
                          ? 'grid-cols-2'
                          : 'grid-cols-1'
                      }`}
                    >
                      {isSafeUrl(project.frozen_repo_link) && (
                        <div className="px-3 py-2">
                          <p className="text-xs text-muted-foreground mb-0.5">Repository</p>
                          <a
                            href={project.frozen_repo_link!}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate block"
                          >
                            {project.frozen_repo_link}
                          </a>
                        </div>
                      )}
                      {isSafeUrl(project.frozen_demo_link) && (
                        <div className="px-3 py-2">
                          <p className="text-xs text-muted-foreground mb-0.5">Demo</p>
                          <a
                            href={project.frozen_demo_link!}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate block"
                          >
                            {project.frozen_demo_link}
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sibling review statuses */}
                  <div className="px-3 py-2 border-t border-border flex items-center gap-3 text-xs">
                    <span className="text-muted-foreground">Reviews:</span>
                    <SiblingBadge label="Time Audit" review={sibling_statuses.time_audit} />
                    <SiblingBadge label="Requirements" review={sibling_statuses.requirements_check} />
                    <SiblingBadge label="Design" review={sibling_statuses.design_review} />
                    <SiblingBadge label="Build" review={sibling_statuses.build_review} />
                  </div>
                </div>

                {/* Previous reviews from prior ships */}
                {previous_reviews.length > 0 && (
                  <CollapsibleCard
                    title="Previous Reviews"
                    storageKey="build-previous-reviews"
                    summary={
                      <span className="flex items-center gap-1">
                        {[...previous_reviews].reverse().map((r) => (
                          <ReviewStatusBadge
                            key={`${r.ship_id}-${r.review_type}`}
                            status={r.status}
                            className="shrink-0"
                          />
                        ))}
                      </span>
                    }
                    trailing={<Kbd variant="muted">2</Kbd>}
                  >
                    <div className="divide-y divide-border">
                      {previous_reviews.map((r) => (
                        <div key={`${r.ship_id}-${r.review_type}`} className="p-3 space-y-1.5">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2">
                              <ReviewStatusBadge status={r.status} />
                              <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                                {r.review_type === 'requirements_check_review'
                                  ? 'RC'
                                  : r.review_type === 'design_review'
                                    ? 'Design'
                                    : 'Build'}
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground shrink-0">
                              {r.reviewer_display_name && `${r.reviewer_display_name} · `}
                              {r.reviewed_at}
                            </span>
                          </div>
                          {r.feedback && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-0.5">Feedback</p>
                              <p className="text-sm whitespace-pre-wrap">{r.feedback}</p>
                            </div>
                          )}
                          {r.internal_reason && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-0.5">Internal Reason</p>
                              <p className="text-sm whitespace-pre-wrap text-muted-foreground">{r.internal_reason}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CollapsibleCard>
                )}

                {/* Preflight checks */}
                {preflight.length > 0 && <PreflightResults checks={preflight} />}

                {/* Changes since the last DR/BR — re-ship review aid */}
                {project.repo_link && (
                  <RepoDiffCard
                    data={repo_diff}
                    repoLink={project.repo_link}
                    storageKey="build-repo-diff"
                    trailing={<Kbd variant="muted">5</Kbd>}
                  />
                )}

                {/* Journal — all entries shown inline */}
                {allEntries.length > 0 && (
                  <CollapsibleCard
                    title="Journal"
                    storageKey="build-journal"
                    summary={
                      <>
                        Count: {allEntries.length}
                        {' | '}Total: {(allEntries.reduce((s, e) => s + e.total_duration, 0) / 3600).toFixed(1)}h{' | '}
                        Avg:{' '}
                        {(allEntries.reduce((s, e) => s + e.total_duration, 0) / allEntries.length / 3600).toFixed(2)}h
                        {' | '}
                        Range: {(Math.min(...allEntries.map((e) => e.total_duration)) / 3600).toFixed(1)}h –{' '}
                        {(Math.max(...allEntries.map((e) => e.total_duration)) / 3600).toFixed(1)}h
                      </>
                    }
                    defaultOpen
                    trailing={<Kbd variant="muted">4</Kbd>}
                  >
                    <JournalEntriesList entries={allEntries} />
                  </CollapsibleCard>
                )}
              </div>
            </ReviewTabPanel>

            <ReviewTabPanel value="repo">
              {repo_tree && repo_tree.entries?.length > 0 && project.repo_link ? (
                <RepoWorkspace data={repo_tree} repoLink={project.repo_link} />
              ) : (
                <p className="p-6 text-sm text-muted-foreground">No repository tree is available for this ship yet.</p>
              )}
            </ReviewTabPanel>

            <ReviewTabPanel value="watch">
              {/* Mounted only while active so hidden players don't keep playing audio */}
              {view === 'watch' && (
                <WatchPanel
                  links={[
                    { label: 'Shipped demo', url: project.frozen_demo_link },
                    { label: 'Demo video', url: project.demo_video_link },
                    { label: 'Playable URL', url: project.demo_link },
                  ]}
                />
              )}
            </ReviewTabPanel>

            {/* Divider */}
            <div className="w-px shrink-0 bg-border" />

            {/* Right: review form / read-only summary */}
            <div className="w-80 shrink-0 overflow-y-auto p-4 space-y-4">
              {backfill ? (
                <>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Backfill Internal Reason
                  </h3>

                  <div className="flex items-center gap-2">
                    <Badge
                      className={
                        review.status === 'approved'
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
                          : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                      }
                    >
                      {review.status}
                    </Badge>
                    {review.reviewer_display_name && (
                      <span className="text-xs text-muted-foreground">by {review.reviewer_display_name}</span>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                      Internal Reason <span className="text-muted-foreground/60">(not shown to user)</span>
                      <Kbd variant="muted">{modKey}J</Kbd>
                    </label>
                    <Textarea
                      ref={internalReasonRef}
                      value={internalReason}
                      onChange={(e) => setInternalReason(e.target.value)}
                      placeholder="Justify the original decision..."
                      className="h-24 text-sm resize-y"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">
                      Demo Link <span className="text-muted-foreground/60">(optional)</span>
                    </label>
                    <Input
                      type="url"
                      value={demoLinkInput}
                      onChange={(e) => setDemoLinkInput(e.target.value)}
                      placeholder="https://..."
                      className="h-8 text-sm"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">
                      Modify Hours <span className="text-muted-foreground/60">(not shown to user)</span>
                    </label>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground font-mono whitespace-nowrap">
                        {userFacingHours.toFixed(1)}h
                      </span>
                      <span className="text-muted-foreground">→</span>
                      <Input
                        type="number"
                        step="0.5"
                        value={hoursAdjInput}
                        onChange={(e) => setHoursAdjInput(e.target.value)}
                        placeholder="0"
                        className="h-8 text-sm font-mono w-20 text-center"
                      />
                      <span className="text-muted-foreground">→</span>
                      <span
                        className={`font-mono whitespace-nowrap ${hoursAdj !== 0 ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}
                      >
                        {internalHours.toFixed(1)}h
                      </span>
                    </div>
                  </div>

                  <Separator />

                  {review.feedback && (
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">
                        Feedback <span className="text-muted-foreground/60">(locked)</span>
                      </label>
                      <p className="text-sm whitespace-pre-wrap rounded-md border border-border bg-muted/40 px-2.5 py-2">
                        {review.feedback}
                      </p>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">Gold (locked):</span>
                    <span className="font-mono">
                      {finalGold}
                      {review.gold_adjustment != null && review.gold_adjustment !== 0
                        ? ` (${review.gold_adjustment >= 0 ? '+' : ''}${review.gold_adjustment} adj)`
                        : ''}
                    </span>
                  </div>

                  <div className="pt-2">
                    <Button
                      className="w-full"
                      variant="default"
                      disabled={submitting || !internalReason.trim()}
                      onClick={handleBackfillSave}
                    >
                      {submitting ? (
                        <LoaderIcon className="size-4 animate-spin mr-1" />
                      ) : (
                        <CheckIcon data-icon="inline-start" />
                      )}
                      Save Backfill
                      <Kbd className="ml-1">{modKey}↵</Kbd>
                    </Button>
                  </div>
                </>
              ) : isTerminal ? (
                <>
                  <div className="space-y-2">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Review Complete
                    </h3>
                    <Badge
                      className={
                        review.status === 'approved'
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
                          : review.status === 'returned'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400'
                            : review.status === 'rejected'
                              ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400'
                              : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                      }
                    >
                      {review.status}
                    </Badge>
                    {review.reviewer_display_name && (
                      <p className="text-xs text-muted-foreground">by {review.reviewer_display_name}</p>
                    )}
                  </div>
                  {review.internal_reason && (
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Internal Reason</label>
                      <p className="text-sm whitespace-pre-wrap">{review.internal_reason}</p>
                    </div>
                  )}
                  {review.feedback && (
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Feedback</label>
                      <p className="text-sm whitespace-pre-wrap">{review.feedback}</p>
                    </div>
                  )}
                  {(review.hours_adjustment != null || review.gold_adjustment != null) && (
                    <div className="space-y-1.5 pt-1">
                      {review.hours_adjustment != null && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-muted-foreground">Hours adj:</span>
                          <span className="font-mono">
                            {review.hours_adjustment >= 0 ? '+' : ''}
                            {(review.hours_adjustment / 3600).toFixed(1)}h
                          </span>
                        </div>
                      )}
                      {review.gold_adjustment != null && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-muted-foreground">Gold adj:</span>
                          <span className="font-mono">
                            {review.gold_adjustment >= 0 ? '+' : ''}
                            {review.gold_adjustment}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Submit Review</h3>

                  <div className="space-y-2">
                    <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                      Internal Reason <span className="text-muted-foreground/60">(not shown to user)</span>
                      <Kbd variant="muted">{modKey}J</Kbd>
                    </label>
                    <Textarea
                      ref={internalReasonRef}
                      value={internalReason}
                      onChange={(e) => setInternalReason(e.target.value)}
                      placeholder="Justify your decision..."
                      className="h-20 text-sm resize-y"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                      Feedback <span className="text-muted-foreground/60">(shown to user)</span>
                      <Kbd variant="muted">{modKey}F</Kbd>
                    </label>
                    <SlackFeedbackComposer
                      value={feedback}
                      onChange={setFeedback}
                      textareaRef={feedbackRef}
                      project={project}
                      mentions={feedbackMentions}
                      onMentionsChange={setFeedbackMentions}
                      reviewerSlack={reviewer_slack}
                      postToSlack={postToSlack}
                      onPostToSlackChange={setPostToSlack}
                      draftKey={`build_review-feedback-draft:${review.id}`}
                    />
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <label className="text-xs text-muted-foreground">
                        Demo Link <span className="text-muted-foreground/60">(optional)</span>
                      </label>
                      <Input
                        type="url"
                        value={demoLinkInput}
                        onChange={(e) => setDemoLinkInput(e.target.value)}
                        placeholder="https://..."
                        className="h-8 text-sm"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs text-muted-foreground">
                        Modify Hours <span className="text-muted-foreground/60">(not shown to user)</span>
                      </label>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground font-mono whitespace-nowrap">
                          {userFacingHours.toFixed(1)}h
                        </span>
                        <span className="text-muted-foreground">→</span>
                        <Input
                          type="number"
                          step="0.5"
                          value={hoursAdjInput}
                          onChange={(e) => setHoursAdjInput(e.target.value)}
                          placeholder="0"
                          className="h-8 text-sm font-mono w-20 text-center"
                        />
                        <span className="text-muted-foreground">→</span>
                        <span
                          className={`font-mono whitespace-nowrap ${hoursAdj !== 0 ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}
                        >
                          {internalHours.toFixed(1)}h
                        </span>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs text-muted-foreground">
                        Modify Gold <span className="text-muted-foreground/60">(shown to user)</span>
                      </label>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground font-mono whitespace-nowrap">
                          {baseGold}
                          <span className="text-[10px] ml-0.5 opacity-60">{userFacingHours}h×7</span>
                        </span>
                        <span className="text-muted-foreground">→</span>
                        <Input
                          type="number"
                          step="1"
                          value={goldAdjInput}
                          onChange={(e) => setGoldAdjInput(e.target.value)}
                          placeholder="0"
                          className="h-8 text-sm font-mono w-20 text-center"
                        />
                        <span className="text-muted-foreground">→</span>
                        <span
                          className={`font-mono whitespace-nowrap ${goldAdj !== 0 ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}
                        >
                          {finalGold}
                        </span>
                      </div>
                    </div>

                    {pending_conversion_koi > 0 && (
                      <div className="rounded-md border border-border bg-muted/40 px-2.5 py-2 text-xs space-y-0.5">
                        <p className="text-muted-foreground">Approval will convert koi to gold</p>
                        <p className="font-mono text-foreground">
                          {pending_conversion_koi} koi → {pending_conversion_koi} gold
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 pt-2">
                    <Button
                      className="w-full"
                      variant="raised-success"
                      disabled={submitting}
                      onClick={() => handleSubmit('approved')}
                    >
                      {submitting ? (
                        <LoaderIcon className="size-4 animate-spin mr-1" />
                      ) : (
                        <CheckIcon data-icon="inline-start" />
                      )}
                      Approve
                      <Kbd className="ml-1 border-emerald-400 bg-emerald-600 text-emerald-50">{modKey}P</Kbd>
                    </Button>

                    <Button
                      className="w-full"
                      variant="raised-warning"
                      disabled={submitting || !feedback.trim()}
                      onClick={() => handleSubmit('returned')}
                      title={!feedback.trim() ? 'Feedback is required when returning' : undefined}
                    >
                      Return (Needs Changes)
                      <Kbd variant="muted" className="ml-1">
                        {modKey}E
                      </Kbd>
                    </Button>

                    {can.swap_type && (
                      <AlertDialog open={designDialogOpen} onOpenChange={setDesignDialogOpen}>
                        <AlertDialogTrigger asChild>
                          <Button className="w-full justify-between" variant="ghost" size="sm" disabled={submitting}>
                            Move to Design Review
                            <Kbd variant="muted">{modKey}D</Kbd>
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Move to Design Review?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This ship will be moved into the Design Review queue. Queued-at timestamp and current
                              in-progress fields (feedback, internal reason, hours/currency adjustments) are preserved.
                              This replaces the Build Review record.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => swap_type_path && router.post(swap_type_path)}>
                              Move to Design Review
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </ReviewTabsRoot>
      <ShortcutHelpDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} entries={shortcutEntries} />
    </>
  )
}

BuildReviewsShow.layout = (page: ReactNode) => <ReviewLayout>{page}</ReviewLayout>
